import { Injectable } from '@angular/core';
import { UserPreferencesService } from './userPreferences.service';
import { Observable, forkJoin, of } from 'rxjs';
import {
  FEATURE,
  GROUP,
  LegacyWatchList,
  METADATA_KEY,
  UserPreferences,
} from '@models/user-preferences';
import { map, mergeMap } from 'rxjs/operators';
import { Watchlist, WatchlistItem } from '@models/watch-list.class';

const DEFAULT_ORDER_ID = 1;

@Injectable()
export class MigrateUserPreferencesService {
  constructor(private userPreferences: UserPreferencesService) {}

  migrate(): Observable<UserPreferences[]> {
    return this.userPreferences.getPreference(FEATURE.watchlist).pipe(
      mergeMap((response) => {
        const isMigrated = response && response.length > 0;

        if (isMigrated) {
          return of(response);
        }

        return this.startMigration();
      }),
    );
  }

  private startMigration(): Observable<UserPreferences[]> {
    let oldWatchlist: UserPreferences | null;

    return this.getOldWatchlist()
      .pipe(
        mergeMap((oldWatchlistResponse) => {
          oldWatchlist = oldWatchlistResponse;
          return this.getLegacyWatchlist();
        }),
      )
      .pipe(
        mergeMap((legacyWatchlistResponse) => {
          let issuers: WatchlistItem[] = [];

          if (oldWatchlist) {
            const metaIssuers = Watchlist.getMetaValue(
              oldWatchlist,
              GROUP.watchlist,
              METADATA_KEY.issuers,
            );

            issuers = Watchlist.parseIssuers(metaIssuers ?? '');
          }

          const firstWatchlist = legacyWatchlistResponse.find(
            (tmpWatchlist) => tmpWatchlist.order === DEFAULT_ORDER_ID,
          );

          if (legacyWatchlistResponse.length && firstWatchlist) {
            const legacyIssuers = Watchlist.parseIssuers(
              firstWatchlist.watch_list ?? '',
            );

            const newIssuers = legacyIssuers;

            issuers.forEach(([securityId, marketId]) => {
              const index = newIssuers.findIndex(
                ([tmpSecurity, tmpMarket]) =>
                  securityId === tmpSecurity &&
                  marketId.toLowerCase() === tmpMarket.toLowerCase(),
              );

              if (index === -1) {
                newIssuers.push([securityId, marketId]);
              }
            });

            return this.migrateWatchlist(firstWatchlist, newIssuers)
              .pipe(
                mergeMap((response) => {
                  if (legacyWatchlistResponse.length > 1) {
                    const filteredWatchlist = legacyWatchlistResponse.filter(
                      (watchlist) => watchlist.order !== DEFAULT_ORDER_ID,
                    );

                    return forkJoin(
                      filteredWatchlist.map((tmpWatchlist) =>
                        this.migrateWatchlist(tmpWatchlist),
                      ),
                    ).pipe(
                      map((responses) => {
                        const allWatchlist = responses;
                        allWatchlist.unshift(response);
                        return allWatchlist;
                      }),
                    );
                  }

                  return of([response]);
                }),
              )
              .pipe(
                mergeMap((preferences) => {
                  if (oldWatchlist) {
                    return this.userPreferences
                      .deletePreference(FEATURE.trading, oldWatchlist.id)
                      .pipe(map(() => preferences));
                  }

                  return of(preferences);
                }),
              );
          }

          const tmpLegacy: LegacyWatchList = {
            name: 'Watchlist',
            order: DEFAULT_ORDER_ID,
            watch_list: '',
          };

          return this.migrateWatchlist(tmpLegacy, issuers)
            .pipe(map((response) => [response]))
            .pipe(
              mergeMap((preferences) => {
                if (oldWatchlist) {
                  return this.userPreferences
                    .deletePreference(FEATURE.trading, oldWatchlist.id)
                    .pipe(map(() => preferences));
                }

                return of(preferences);
              }),
            );
        }),
      );
  }

  private migrateWatchlist(
    legacy: LegacyWatchList,
    issuers?: WatchlistItem[],
  ): Observable<UserPreferences> {
    let issuersValue = '';

    if (issuers) {
      issuersValue = issuers.length
        ? issuers.map((item) => item.join('|')).join(',')
        : '';
    } else {
      issuersValue = legacy.watch_list ?? '';
    }

    const preference: UserPreferences = {
      group_id: `${GROUP.watchlist}${legacy.order}`,
      metadata: [
        {
          key: METADATA_KEY.name,
          value: legacy.name ?? 'Watchlist',
        },
        {
          key: METADATA_KEY.order,
          value: `${legacy.order}`,
        },
        {
          key: METADATA_KEY.isMigrated,
          value: 'true',
        },
        {
          key: METADATA_KEY.issuers,
          value: issuersValue,
        },
      ],
    };

    return this.userPreferences.createPreference(FEATURE.watchlist, preference);
  }

  private getLegacyWatchlist(): Observable<LegacyWatchList[]> {
    return this.userPreferences.getLegacyWatchList();
  }

  private getOldWatchlist(): Observable<UserPreferences | null> {
    return this.userPreferences.getPreference(FEATURE.trading).pipe(
      map((response) => {
        const watchlist = Watchlist.getPreferenceByGroup(
          response,
          GROUP.watchlist,
        );

        return watchlist ?? null;
      }),
    );
  }
}
